import { Table } from "antd";
import React, { FC, useMemo, useState } from "react";
import {
  useGetProductionAnalysisQuery,
  useGetAllMachinesWithNoParamsQuery,
  useGetUsersQuery,
} from "shared/api";
import {
  useDebouncedTableFilter,
  useDateFilters,
  useProductionAnalysisTableColumns,
  usePagination,
} from "shared/hooks";
import { getQueryParam, updateQueryParam } from "shared/lib";
import { RenderClearFiltersButton } from "shared/ui";

const ProductionAnalysisTable: FC = () => {
  const { data: machines } = useGetAllMachinesWithNoParamsQuery();
  const { data: usersResponse } = useGetUsersQuery({ limit: 1000 });

  const adaptedMachines = useMemo(() => {
    return (
      machines?.map((machine) => ({
        label: machine.name,
        value: machine.id,
      })) || []
    );
  }, [machines]);

  const adaptedUsers = useMemo(() => {
    return (
      usersResponse?.results?.map((user) => ({
        label: user.email,
        value: user.id || 0,
      })) || []
    );
  }, [usersResponse]);

  const { pageSize, setPageSize, currentPage, setCurrentPage, offset } =
    usePagination(20);

  const {
    debouncedValue: debouncedOrderCodeValue,
    handleFilterChange: handleOrderCodeValue,
    value: orderCodeValue,
    clearFilterValue: clearOrderCodeValue,
  } = useDebouncedTableFilter("orderCode", 400);

  const {
    dateGreater: timestampGreater,
    dateLesser: timestampLesser,
    handleDateGreater: handleTimestampGreater,
    handleDateLesser: handleTimestampLesser,
    clearDates: clearTimestampFilter,
    isDataFilterActive: isTimestampFilterActive,
  } = useDateFilters();

  const [machineFilter, setMachineFilter] = useState<number | string>(
    getQueryParam("machine") || ""
  );
  const [userFilter, setUserFilter] = useState<number | string>(
    getQueryParam("user") || ""
  );

  const handleMachineFilter = (value: number | string) => {
    setMachineFilter(value);
    updateQueryParam("machine", value.toString());
  };

  const handleUserFilter = (value: number | string) => {
    setUserFilter(value);
    updateQueryParam("user", value.toString());
  };

  const {
    debouncedValue: debouncedSpeedMinValue,
    handleFilterChange: handleSpeedMinValue,
    value: speedMinValue,
    clearFilterValue: clearSpeedMinValue,
  } = useDebouncedTableFilter("speedMin", 400);

  const {
    debouncedValue: debouncedSpeedMaxValue,
    handleFilterChange: handleSpeedMaxValue,
    value: speedMaxValue,
    clearFilterValue: clearSpeedMaxValue,
  } = useDebouncedTableFilter("speedMax", 400);

  const {
    data: productionAnalysis,
    isLoading,
    isFetching,
  } = useGetProductionAnalysisQuery({
    expand:
      "order_process_step_tracker,order_process_step_tracker.process_step,order_process_step_tracker.order,order_process_step_tracker__order,order_process_step_tracker__process_step,machine,user",
    offset,
    limit: pageSize,
    order_process_step_tracker__order__order_code:
      debouncedOrderCodeValue || undefined,
    timestamp__gte: timestampGreater || undefined,
    timestamp__lte: timestampLesser || undefined,
    machine: machineFilter ? Number(machineFilter) : undefined,
    user: userFilter ? Number(userFilter) : undefined,
    detected_speed__gte: debouncedSpeedMinValue
      ? Number(debouncedSpeedMinValue)
      : undefined,
    detected_speed__lte: debouncedSpeedMaxValue
      ? Number(debouncedSpeedMaxValue)
      : undefined,
  });

  const columns = useProductionAnalysisTableColumns({
    orderCodeValue,
    handleOrderCodeValue,
    clearOrderCodeValue,
    handleDateGreater: handleTimestampGreater,
    handleDateLesser: handleTimestampLesser,
    isDateFilterActive: isTimestampFilterActive,
    machineFilter,
    handleMachineFilter,
    adaptedMachines,
    userFilter,
    handleUserFilter,
    adaptedUsers,
    speedMinValue,
    handleSpeedMinValue,
    clearSpeedMinValue,
    speedMaxValue,
    handleSpeedMaxValue,
    clearSpeedMaxValue,
  });

  const isVisibleClearFiltersButton =
    !!debouncedOrderCodeValue ||
    !!timestampGreater ||
    !!timestampLesser ||
    !!machineFilter ||
    !!userFilter ||
    !!debouncedSpeedMinValue ||
    !!debouncedSpeedMaxValue;

  const clearAllFilters = () => {
    clearOrderCodeValue();
    clearTimestampFilter();
    handleMachineFilter("");
    handleUserFilter("");
    clearSpeedMinValue();
    clearSpeedMaxValue();
  };

  return (
    <div className="mt-3">
      {isVisibleClearFiltersButton && (
        <RenderClearFiltersButton onClick={clearAllFilters} />
      )}

      <Table
        loading={isLoading || isFetching}
        columns={columns}
        dataSource={productionAnalysis?.results}
        rowKey={(record) => record.id}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage,
          onShowSizeChange(page, pageSize) {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
          onChange(page) {
            setCurrentPage(page);
          },
          total: productionAnalysis?.count,
        }}
      />
    </div>
  );
};

export { ProductionAnalysisTable };
